import React from 'react';
import { Box, Chip, FormControl, Grid, InputLabel, MenuItem, Select, Stack, Typography, FormHelperText, TextField, Autocomplete } from '@mui/material';
import { useFormContext, Controller } from 'react-hook-form';
import LocationAutocomplete from '../../components/LocationAutocomplete';
import {
  skillOptions,
  experienceOptions,
  availabilityOptions,
  salaryOptions,
  visaOptions,
  transportOptions,
  yesNoOptions,
  englishLevelOptions,
  countryOptions,
  languageOptions,
  commonRequirements,
  skillRequirementsMap
} from '../../constants/dropdownOptions';

// Skills list for display (using labels from centralized options)
const skills = skillOptions.map(option => option.label);

const FieldSet = ({ children }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: (theme) => theme.spacing(3),
      width: '100%',
      padding: (theme) => theme.spacing(3),
      borderRadius: (theme) => theme.shape.borderRadius,
      backgroundColor: (theme) => theme.palette.background.paper,
    }}
  >
    {children}
  </Box>
);

const JobSeekerStep = () => {
  const { control, watch, setValue, formState: { errors } } = useFormContext();
  const jobseekerErrors = errors.jobseeker || {};
  const selectedSkills = watch('jobseeker.skills') || [];
  const selectedLabourRequirements = watch('jobseeker.labourRequirements') || [];

  // Get filtered labour requirements based on selected skills
  const getFilteredLabourRequirementsForSkills = (skills) => {
    if (skills.length === 0) {
      return commonRequirements;
    }
    
    const relevantRequirements = new Set(commonRequirements);
    
    skills.forEach(skill => {
      if (skillRequirementsMap[skill]) {
        skillRequirementsMap[skill].forEach(req => relevantRequirements.add(req));
      }
    });
    
    return Array.from(relevantRequirements).sort();
  };

  const getFilteredLabourRequirements = () => {
    return getFilteredLabourRequirementsForSkills(selectedSkills);
  };

  const filteredLabourRequirements = getFilteredLabourRequirements();

  const handleSkillToggle = (skillLabel) => {
    // Find the skill value from the label
    const skillOption = skillOptions.find(option => option.label === skillLabel);
    const skillValue = skillOption ? skillOption.value : skillLabel;
    
    const newSkills = selectedSkills.includes(skillValue)
      ? selectedSkills.filter((s) => s !== skillValue)
      : [...selectedSkills, skillValue];
    setValue('jobseeker.skills', newSkills);
    
    // Clean up labour requirements that are no longer relevant
    const newFilteredRequirements = getFilteredLabourRequirementsForSkills(newSkills);
    const cleanedLabourRequirements = selectedLabourRequirements.filter(req => 
      newFilteredRequirements.includes(req)
    );
    setValue('jobseeker.labourRequirements', cleanedLabourRequirements);
  };

  const handleLabourRequirementToggle = (requirement) => {
    const newRequirements = selectedLabourRequirements.includes(requirement)
      ? selectedLabourRequirements.filter((r) => r !== requirement)
      : [...selectedLabourRequirements, requirement];
    setValue('jobseeker.labourRequirements', newRequirements);
  };

  const handleLocationChange = (locationDetails) => {
    if (!locationDetails) {
      setValue('jobseeker.location', '');
      setValue('jobseeker.suburb', '');
      setValue('jobseeker.postcode', '');
      setValue('jobseeker.state', '');
      setValue('jobseeker.lat', null);
      setValue('jobseeker.lng', null);
      return;
    }

    setValue('jobseeker.location', locationDetails.description);
    setValue('jobseeker.suburb', locationDetails.suburb);
    setValue('jobseeker.postcode', locationDetails.postcode);
    setValue('jobseeker.state', locationDetails.state);
    setValue('jobseeker.lat', locationDetails.lat);
    setValue('jobseeker.lng', locationDetails.lng);
  };

  return (
    <Stack spacing={3}>
      <Stack spacing={1} alignItems="center">
        <Typography variant="h4" color="text.primary">Work Profile</Typography>
        <Typography color="text.secondary">Help employers find the right match</Typography>
      </Stack>

      <FieldSet>
        <Grid container spacing={3}>
          <Grid size={12}>
            <FormControl fullWidth error={!!jobseekerErrors.skills}>
              <InputLabel shrink id="skills-label">Skills & Expertise *</InputLabel>
              <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {skills.map((skillLabel) => {
                  const skillOption = skillOptions.find(option => option.label === skillLabel);
                  const skillValue = skillOption ? skillOption.value : skillLabel;
                  
                  return (
                    <Chip
                      key={skillLabel}
                      label={skillLabel}
                      onClick={() => handleSkillToggle(skillLabel)}
                      data-testid={`skill-chip-${skillLabel.toLowerCase().replace(/\s+/g, '-')}`}
                      role="button"
                      aria-pressed={selectedSkills.includes(skillValue)}
                      variant={selectedSkills.includes(skillValue) ? 'filled' : 'outlined'}
                      sx={{
                        fontSize: '14px',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                        padding: 2,
                        ...(selectedSkills.includes(skillValue) ? {
                          bgcolor: 'primary.main',
                          color: 'primary.contrastText',
                          fontWeight: 600,
                          boxShadow: '0 6px 18px rgba(16,24,40,0.08)',
                          transform: 'translateY(-2px)'
                        } : {}),
                        '&:hover': { transform: 'scale(1.05)' },
                      }}
                    />
                  );
                })}
              </Box>
              {jobseekerErrors.skills && (
                <FormHelperText>{jobseekerErrors.skills.message}</FormHelperText>
              )}
            </FormControl>
          </Grid>

          <Grid size={12}>
            <FormControl fullWidth error={!!jobseekerErrors.labourRequirements}>
              <InputLabel shrink id="labour-requirements-label">General Labour Requirements</InputLabel>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
                Select all certifications, licenses, and requirements you have
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                 {filteredLabourRequirements.map((requirement) => (
                   <Chip
                     key={requirement}
                     label={requirement}
                     onClick={() => handleLabourRequirementToggle(requirement)}
                     data-testid={`requirement-chip-${requirement.toLowerCase().replace(/\s+/g, '-')}`}
                     role="button"
                     aria-pressed={selectedLabourRequirements.includes(requirement)}
                     variant={selectedLabourRequirements.includes(requirement) ? 'filled' : 'outlined'}
                     sx={{
                       fontSize: '13px',
                       cursor: 'pointer',
                       transition: 'all 0.15s ease',
                       padding: 1.5,
                       ...(selectedLabourRequirements.includes(requirement) ? {
                         bgcolor: 'secondary.main',
                         color: 'secondary.contrastText',
                         fontWeight: 600,
                         boxShadow: '0 6px 18px rgba(16,24,40,0.06)',
                         transform: 'translateY(-2px)'
                       } : {}),
                       '&:hover': { transform: 'scale(1.05)' },
                     }}
                   />
                 ))}
              </Box>
              {jobseekerErrors.labourRequirements && (
                <FormHelperText>{jobseekerErrors.labourRequirements.message}</FormHelperText>
              )}
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth error={!!jobseekerErrors.experience}>
              <InputLabel id="experience-label">Experience Level *</InputLabel>
              <Controller
                name="jobseeker.experience"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <Select data-testid="jobseeker-experience-select" sx={{ bgcolor: 'background.input' }} {...field} labelId="experience-label" label="Experience Level *">
                    {experienceOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
              {jobseekerErrors.experience && (
                <FormHelperText>{jobseekerErrors.experience.message}</FormHelperText>
              )}
            </FormControl>
          </Grid>
          
          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth error={!!jobseekerErrors.availability}>
              <InputLabel id="availability-label">Availability *</InputLabel>
              <Controller
                name="jobseeker.availability"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <Select data-testid="jobseeker-availability-select" sx={{ bgcolor: 'background.input' }} {...field} labelId="availability-label" label="Availability *">
                    {availabilityOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
              {jobseekerErrors.availability && (
                <FormHelperText>{jobseekerErrors.availability.message}</FormHelperText>
              )}
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth error={!!jobseekerErrors.expectedSalary}>
              <InputLabel id="salary-label">Expected Hourly Rate *</InputLabel>
              <Controller
                name="jobseeker.expectedSalary"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <Select data-testid="jobseeker-salary-select" sx={{ bgcolor: 'background.input' }} {...field} labelId="salary-label" label="Expected Hourly Rate *">
                    {salaryOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
              {jobseekerErrors.expectedSalary && (
                <FormHelperText>{jobseekerErrors.expectedSalary.message}</FormHelperText>
              )}
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth error={!!jobseekerErrors.visaStatus}>
              <InputLabel id="visa-label">Visa Status *</InputLabel>
              <Controller
                name="jobseeker.visaStatus"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <Select data-testid="jobseeker-visa-select" sx={{ bgcolor: 'background.input' }} {...field} labelId="visa-label" label="Visa Status *">
                    {visaOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
              {jobseekerErrors.visaStatus && (
                <FormHelperText>{jobseekerErrors.visaStatus.message}</FormHelperText>
              )}
            </FormControl>
          </Grid>
          
          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth error={!!jobseekerErrors.location}>
              <Box sx={{ bgcolor: 'background.input', borderRadius: 1 }}>
                <LocationAutocomplete 
                  onLocationSelect={handleLocationChange} 
                  initialValue={watch('jobseeker.location')}
                  required
                />
              </Box>
              {jobseekerErrors.location && (
                <FormHelperText>{jobseekerErrors.location.message}</FormHelperText>
              )}
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth error={!!jobseekerErrors.transport}>
              <InputLabel id="transport-label">Transport *</InputLabel>
              <Controller
                name="jobseeker.transport"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <Select data-testid="jobseeker-transport-select" sx={{ bgcolor: 'background.input' }} {...field} labelId="transport-label" label="Transport *">
                    {transportOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
              {jobseekerErrors.transport && (
                <FormHelperText>{jobseekerErrors.transport.message}</FormHelperText>
              )}
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth error={!!jobseekerErrors.hasValidABN}>
              <InputLabel id="hasValidABN-label">Do you have a valid ABN? *</InputLabel>
              <Controller
                name="jobseeker.hasValidABN"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <Select data-testid="jobseeker-abnaryn-select" sx={{ bgcolor: 'background.input' }} {...field} labelId="hasValidABN-label" label="Do you have a valid ABN? *">
                    {yesNoOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
              {jobseekerErrors.hasValidABN && (
                <FormHelperText>{jobseekerErrors.hasValidABN.message}</FormHelperText>
              )}
            </FormControl>
          </Grid>

          {watch('jobseeker.hasValidABN') === 'yes' && (
            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                name="jobseeker.abnNumber"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    data-testid="jobseeker-abnnumber-input"
                    label="ABN Number"
                    fullWidth
                    sx={{ bgcolor: 'background.input' }}
                    error={!!jobseekerErrors.abnNumber}
                    helperText={jobseekerErrors.abnNumber?.message}
                  />
                )}
              />
            </Grid>
          )}

          <Grid size={12}>
            <Typography variant="subtitle1" fontWeight="medium" color="text.secondary" sx={{ mt: 1 }}>
              Language & Background (Optional)
            </Typography>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Controller
              name="jobseeker.countryOfOrigin"
              control={control}
              defaultValue=""
              render={({ field: { onChange, value, ...field } }) => (
                <Autocomplete
                  {...field}
                  value={value || null}
                  onChange={(_, newValue) => onChange(newValue || '')}
                  options={countryOptions}
                  freeSolo
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Country of Origin"
                      sx={{ bgcolor: 'background.input' }}
                      placeholder="Start typing…"
                    />
                  )}
                />
              )}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Controller
              name="jobseeker.nativeLanguage"
              control={control}
              defaultValue=""
              render={({ field: { onChange, value, ...field } }) => (
                <Autocomplete
                  {...field}
                  value={value || null}
                  onChange={(_, newValue) => onChange(newValue || '')}
                  options={languageOptions}
                  freeSolo
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Native Language"
                      sx={{ bgcolor: 'background.input' }}
                      placeholder="Start typing…"
                    />
                  )}
                />
              )}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth>
              <InputLabel id="englishLevel-label">English Level</InputLabel>
              <Controller
                name="jobseeker.englishLevel"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <Select sx={{ bgcolor: 'background.input' }} {...field} labelId="englishLevel-label" label="English Level">
                    {englishLevelOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </FormControl>
          </Grid>
        </Grid>
      </FieldSet>
    </Stack>
  );
};

export default JobSeekerStep;
